import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { toast } from '../components/Toast.js';
import { useSession } from '../hooks/useSession.js';
import { applyAccentColor } from '../components/SettingsEffects.js';
import { HelpLink } from '../components/HelpLink.js';
import { OrganizationsPanel } from '../components/OrganizationsPanel.js';
import { GlobalRolesPanel } from '../components/GlobalRolesPanel.js';
import { UsersPanel } from '../components/UsersPanel.js';
import { GroupsPanel } from '../components/GroupsPanel.js';
import { ToolsPanel } from '../components/ToolsPanel.js';
import { ToolPalettePanel } from '../components/ToolPalettePanel.js';
import { BundlesPanel } from '../components/BundlesPanel.js';
import { PatsPanel } from '../components/PatsPanel.js';
import { AuditPanel } from '../components/AuditPanel.js';
import { RecycleBinPanel } from '../components/RecycleBinPanel.js';
import { SearchTuningSection } from '../components/SearchTuningSection.js';
import { SearchHealthCard } from '../components/SearchHealthCard.js';
import { CardEffectField } from '../components/documents/CardEffectField.js';

type Section = 'general' | 'documents' | 'search' | 'data-pipeline' | 'notifications' | 'permissions' | 'organizations' | 'global-roles' | 'users' | 'groups' | 'tools' | 'tool-palette' | 'bundles' | 'pats' | 'audit' | 'recycle-bin';
// CTSK-298 Phase 1 (Pattern B) — `disabled: true` marks tabs whose
// features are non-functional / shell-only on v0.1.116 (Notifications
// unplanned, Permissions incomplete; the OCR + Barcode tabs were removed
// in CTSK-523). Disabled tabs stay VISIBLE for evaluator discoverability
// (Pattern A hide-entirely was rejected) but render grayed + badged +
// non-clickable, grouped to the right of a divider. Their SectionFields
// cases + server-side /settings/<section> envelopes are intentionally
// kept — Phase 2/3 (regrouping, roadmap enablement) revive them.
const SECTIONS: { value: Section; label: string; disabled?: boolean }[] = [
  { value: 'general', label: 'General' },
  { value: 'documents', label: 'Documents' },
  // CTSK-523 — search settings split out of Documents. UI-only: the Search
  // tab reads/writes the SAME `documents` settings envelope (see
  // envelopeSectionFor), so no setting key moves or is renamed.
  { value: 'search', label: 'Search' },
  // 0.1.7 — Users management. Separate tab from Organizations because
  // users exist independently of org memberships and need their own
  // create / enable / disable surface. Gated server-side on users:view
  // (list) / users:create (mint) / users:manage (update).
  { value: 'users', label: 'Users' },
  // CTSK-240 (0.1.87) — Groups management. Sits next to Users since
  // both live in the users domain (GET /groups was read-only until
  // now). Gated server-side on users:view (list/detail) /
  // groups:manage (create / edit / delete / membership).
  { value: 'groups', label: 'Groups' },
  { value: 'organizations', label: 'Organizations' },
  // 0.1.7 — cross-org RBAC management. Separate tab from Organizations
  // because the table shape + caps differ: orgs is per-membership and
  // anyone can view their own; global-roles is grant-scoped and gated
  // on roles:manage.
  { value: 'global-roles', label: 'Global Roles' },
  // 0.1.8 — Tools tab. First-class extensions bound to a nest with
  // a dedicated storage container for their registry.yaml.
  { value: 'tools', label: 'Tools' },
  // FR 2026-05-29 — Tool Palette experiment. Per-org visibility
  // toggles for the 9-button action strip on DocumentDetail. Mockup —
  // localStorage-backed for v1; backend persistence deferred.
  { value: 'tool-palette', label: 'Tool Palette' },
  { value: 'bundles', label: 'Bundles' },
  // 0.1.40 — Personal Access Tokens tab. Self-scoped: pats:manage is
  // granted to every role by default because the routes hard-filter
  // to the caller's own tokens server-side. See PatsPanel + docs/pats/api.md.
  { value: 'pats', label: 'Personal Access Tokens' },
  // CTSK-389 — Audit tab. Reads resource_audit (populated since CTSK-
  // 386/387); gated server-side on audit:view (admin-default cap).
  { value: 'audit', label: 'Audit' },
  // CTSK-333 (Phase 3 of EPIC CTSK-385 — the EPIC's final phase). List +
  // restore + hard-purge soft-deleted rows across all 6 resource types.
  // Sits next to Audit (its data source for "deleted by") in the
  // Identity & Access group. Tab stays visible in the nav for every
  // caller (same convention as Audit/Pats below — server cap is
  // authoritative, nav-hiding isn't this codebase's pattern for admin
  // tabs); RecycleBinPanel's own 403 fallback is what a non-cap-holder
  // actually sees. As of CTSK-421 the underlying list endpoints
  // themselves also require admin:view_deleted (not just the panel).
  { value: 'recycle-bin', label: 'Recycle Bin' },
  // CTSK-523 — reserved slot for a future data-pipeline configuration
  // surface. Static "coming soon" page: always enabled, no gate, no
  // settings envelope, no network requests.
  { value: 'data-pipeline', label: 'Data Pipeline' },
  // CTSK-298 — non-functional group (order matters: these render after
  // the divider at the right end of the strip).
  { value: 'notifications', label: 'Notifications', disabled: true },
  { value: 'permissions', label: 'Permissions', disabled: true },
];

// CTSK-298 — quick lookup so deep links (/settings/notifications etc.) can be
// coerced away from disabled sections instead of rendering their forms.
const DISABLED_SECTIONS: ReadonlySet<Section> = new Set(
  SECTIONS.filter((s) => s.disabled).map((s) => s.value),
);

// CTSK-298 Phase 2 — sectioned tab bar. The 14 tabs group into three
// labeled clusters so a first-time viewer parses the strip as three
// ideas instead of one long run. Labels are display-only (no click,
// no hover); disabled tabs keep their Phase 1 Pattern B treatment but
// now sit inside their topical group (Permissions with the identity
// tabs; the whole Data pipelines group is roadmap). Left rail nav +
// collapsible groups were considered and deferred — see the ticket.
const SECTION_GROUPS: { key: string; label: string; sections: Section[] }[] = [
  { key: 'content', label: 'Content', sections: ['general', 'documents', 'search', 'tools', 'tool-palette', 'bundles'] },
  { key: 'identity', label: 'Identity & Access', sections: ['users', 'groups', 'organizations', 'global-roles', 'pats', 'audit', 'recycle-bin', 'permissions'] },
  { key: 'pipelines', label: 'Data pipelines', sections: ['data-pipeline', 'notifications'] },
];

const SECTION_BY_VALUE: ReadonlyMap<Section, (typeof SECTIONS)[number]> = new Map(
  SECTIONS.map((s) => [s.value, s]),
);

// CTSK-355 — active settings section → HELP_PAGES slug for the
// page-header `?` help link. Sections absent from this map have no
// dedicated help doc yet: the `?` still renders and lands on the
// /help index (the missing-doc set is tracked for follow-up filing —
// see the CTSK-355 ship report). Exported so the Settings test can
// pin the mapping against the live HELP_PAGES catalog.
export const SETTINGS_HELP_SLUGS: Partial<Record<Section, string>> = {
  tools: 'tools',
  bundles: 'bundles',
  pats: 'pats-api',
  'global-roles': 'role-catalog',
  // CTSK-389 — Settings → Audit tab's `?` icon links to the new
  // /help/audit doc.
  audit: 'audit',
  // CTSK-333 (Phase 3) — Settings → Recycle Bin tab's `?` icon links to
  // the new /help/recycle-bin doc.
  'recycle-bin': 'recycle-bin',
  // CTSK-375..380 — Settings sub-page docs batch (EPIC CTSK-374):
  // these six sections now route their `?` directly to a dedicated
  // doc instead of falling back to the /help index.
  general: 'settings-general',
  documents: 'settings-documents',
  // CTSK-523 — Search + Data Pipeline tabs get their own docs.
  search: 'settings-search',
  'data-pipeline': 'settings-data-pipeline',
  users: 'settings-users',
  groups: 'settings-groups',
  organizations: 'settings-organizations',
  'tool-palette': 'settings-tool-palette',
};

// CTSK-346 — deep links arrive as raw strings from the router (the
// sidebar nest-card Tools cell emits /settings?section=tools&nestSlug=…,
// and /settings/<section> path segments pass through the same prop).
// Coerce anything unknown to General, and route disabled sections
// through the existing CTSK-298 fallback, so a junk or stale deep-link
// still lands on a functional tab instead of a broken panel.
function coerceSection(value: string | null | undefined): Section {
  const known = SECTIONS.find((s) => s.value === value);
  if (!known || DISABLED_SECTIONS.has(known.value)) return 'general';
  return known.value;
}

// CTSK-523 — the Search tab is a presentation split of the Documents tab.
// Both tabs read and write the same `documents` envelope on the server
// (GET/PATCH /settings/documents), so every setting key stays where it is.
function envelopeSectionFor(section: Section): string {
  return section === 'search' ? 'documents' : section;
}

// CTSK-523 — keys owned by the Search tab, used so "Reset to defaults" on
// either tab only touches its own half of the shared `documents` envelope.
// `groupingEnabled` is deliberately NOT here: it is shown on the Search tab as
// the gate for label weight + facets but is owned by the Documents tab.
const SEARCH_ENVELOPE_KEYS: readonly string[] = [
  'searchRankWeights', 'searchTrgmFallback', 'searchHeadlineChars', 'searchStatementTimeoutMs',
  'searchSlowMs', 'searchLogQueries', 'searchLabelBoost', 'searchFacetsEnabled', 'neighborhoodMaxPerBasis',
];

interface SettingsEnvelope {
  section: string;
  body: Record<string, unknown>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export function Settings({
  section: initialSection = 'general',
  onNavigate,
  nestSlug = null,
}: {
  // CTSK-346 — widened from Section to string: the router passes raw
  // ?section= / path-segment values straight through, and coerceSection
  // maps unknown or disabled values to 'general' (CTSK-298 behavior).
  section?: string;
  // 0.1.8 — passed through from the router so child panels (Tools)
  // can route to native tool pages and Organization actions can jump
  // to other settings tabs without a full reload.
  onNavigate?: (path: string) => void;
  // CTSK-348 — &nestSlug= from the sidebar nest-card Tools cell deep-link
  // (/settings?section=tools&nestSlug=<slug>, emitted since 0.1.8).
  // Forwarded to ToolsPanel, which filters its table to that nest.
  // Prop-passed (not read from window.location) for the same re-sync
  // reason as `section` above.
  nestSlug?: string | null;
}) {
  const qc = useQueryClient();
  // CTSK-504 — search tuning + health are admin-only (settings:manage).
  // Display-only: gates the search tuning controls / health card. Own
  // query key on purpose — sharing ToolsPanel's `auth-session-caps` key
  // would seed its cache before the Tools tab mounts. Server
  // `settings:manage` gates stay authoritative.
  const capsQ = useQuery({
    queryKey: ['settings-tab-caps'],
    queryFn: () => apiSingle<{ session: { capabilities: string[] } }>('/auth/session'),
  });
  const canManageSettings = (capsQ.data?.session?.capabilities ?? []).includes('settings:manage');
  // CTSK-298 — a deep link (or stale bookmark) into a disabled section
  // falls back to General rather than rendering a non-functional form.
  // CTSK-346 — unknown sections coerce the same way (see coerceSection).
  const [section, setSection] = useState<Section>(() => coerceSection(initialSection));
  // CTSK-346 — clicking a sidebar deep-link while already on /settings
  // changes the URL (and this prop) without remounting the component,
  // so re-sync the active tab when the routed section changes. Manual
  // tab clicks only touch local state and are not stomped by this.
  useEffect(() => {
    setSection(coerceSection(initialSection));
  }, [initialSection]);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);

  // The 'organizations' and 'bundles' tabs are special — they don't
  // load a SettingsEnvelope from /settings/<section>. Disable the
  // query for those tabs; each panel manages its own queries against
  // /orgs, /admin/bundle/*, etc.
  const isOrgsTab = section === 'organizations';
  const isBundlesTab = section === 'bundles';
  // 0.1.7 — Global Roles tab manages cross-org role grants. Same
  // "no SettingsEnvelope" pattern as Organizations / Bundles.
  const isGlobalRolesTab = section === 'global-roles';
  // 0.1.7 — Users tab manages user accounts (create / disable / search).
  const isUsersTab = section === 'users';
  // CTSK-240 (0.1.87) — Groups tab manages groups + memberships. Same
  // "no SettingsEnvelope" panel pattern as Users / Organizations.
  const isGroupsTab = section === 'groups';
  // 0.1.8 — Tools tab manages tool installs (extensions / adapters).
  const isToolsTab = section === 'tools';
  // FR 2026-05-29 — Tool Palette experiment. Self-contained client-only
  // panel (no SettingsEnvelope load); same "panel tab" pattern as the
  // others above.
  const isToolPaletteTab = section === 'tool-palette';
  // 0.1.40 — Personal Access Tokens tab. Self-contained; loads from
  // /pats directly instead of the SettingsEnvelope.
  const isPatsTab = section === 'pats';
  // CTSK-389 — Audit tab. Self-contained; loads from /audit directly
  // instead of the SettingsEnvelope, same "panel tab" pattern as pats.
  const isAuditTab = section === 'audit';
  // CTSK-333 (Phase 3 of EPIC CTSK-385) — Recycle Bin tab. Self-
  // contained; loads from the 6 resource list endpoints directly (plus
  // /audit for "deleted by"), same "panel tab" pattern as pats/audit.
  const isRecycleBinTab = section === 'recycle-bin';
  // CTSK-523 — static placeholder; no envelope, no save/reset.
  const isDataPipelineTab = section === 'data-pipeline';
  const isPanelTab = isDataPipelineTab || isOrgsTab || isBundlesTab || isGlobalRolesTab || isUsersTab || isGroupsTab || isToolsTab || isToolPaletteTab || isPatsTab || isAuditTab || isRecycleBinTab;
  const envelopeSection = envelopeSectionFor(section);
  const q = useQuery({
    queryKey: ['settings', envelopeSection],
    queryFn: () => apiSingle<SettingsEnvelope>(`/settings/${envelopeSection}`),
    enabled: !isPanelTab,
  });

  // Re-seed the draft when the server data OR the tab changes: Documents and
  // Search share one query, so switching between them must not carry unsaved
  // edits across (matches the pre-split behaviour of a fresh load per tab).
  useEffect(() => {
    if (q.data) setDraft({ ...q.data.body });
  }, [q.data, section]);

  const save = useMutation({
    mutationFn: () =>
      apiSingle<SettingsEnvelope>(`/settings/${envelopeSection}`, {
        method: 'PATCH',
        body: draft ?? {},
      }),
    onSuccess: () => { toast.success('Saved'); qc.invalidateQueries({ queryKey: ['settings', envelopeSection] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const reset = useMutation({
    mutationFn: async () => {
      if (section !== 'documents' && section !== 'search') {
        return apiSingle<SettingsEnvelope>(`/settings/${section}/reset`, { method: 'POST' });
      }
      // CTSK-523 — Documents and Search share one envelope, so a plain
      // POST /reset would wipe the OTHER tab's settings too. Reset only this
      // tab's keys: fetch defaults, overlay them on the saved body, PATCH.
      const defaults = await apiSingle<{ section: string; body: Record<string, unknown> }>(`/settings/documents/defaults`);
      const current = q.data?.body ?? {};
      const mine = (k: string) => (SEARCH_ENVELOPE_KEYS.includes(k) ? section === 'search' : section === 'documents');
      const next: Record<string, unknown> = { ...current };
      for (const k of Object.keys(defaults.body)) if (mine(k)) next[k] = defaults.body[k];
      return apiSingle<SettingsEnvelope>(`/settings/documents`, { method: 'PATCH', body: next });
    },
    onSuccess: (data) => {
      toast.success('Reset to defaults');
      setDraft({ ...data.body });
      qc.invalidateQueries({ queryKey: ['settings', envelopeSection] });
    },
  });

  const dirty = !!draft && !!q.data && JSON.stringify(draft) !== JSON.stringify(q.data.body);

  function setField(k: string, v: unknown) {
    setDraft((d) => ({ ...(d ?? {}), [k]: v }));
  }

  const sectionLabel = SECTION_BY_VALUE.get(section)?.label ?? 'Settings';

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-subtitle">App-wide configuration</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* CTSK-355 — page-header `?` help link, mapped to the ACTIVE
              section's help doc (SETTINGS_HELP_SLUGS); sections without
              a dedicated doc land on the /help index. Rendered for
              every section so each settings sub-page carries the
              affordance. */}
          <HelpLink
            pageName={`Settings — ${sectionLabel}`}
            slug={SETTINGS_HELP_SLUGS[section] ?? null}
            onNavigate={onNavigate}
          />
          {!isPanelTab && (
            <>
              <button className="btn" onClick={() => reset.mutate()}>Reset to defaults</button>
              <button className="btn" disabled={!dirty} onClick={() => setDraft({ ...(q.data?.body ?? {}) })}>Discard</button>
              <button className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* CTSK-298 — `tabs--settings` scopes the Pattern B + stronger
          active-highlight styling to THIS strip only; the base
          .tabs/.tab classes are shared with Watching, FileTasks and
          DocumentDetail and must not change.
          Phase 2 — the strip is sectioned into three labeled groups
          (Option A: labels ABOVE the strip, left-aligned with each
          group's first tab). Each group is a column (label row + tab
          row) so every tab row bottoms out on the strip's shared
          border and the active-tab underline stays visually anchored
          exactly as Phase 1 shipped it. */}
      <div className="tabs tabs--settings tabs--grouped">
        {SECTION_GROUPS.map((g) => (
          <div key={g.key} className="tab-group">
            {/* Display-only label: semantic heading, no interaction. */}
            <span className="tab-group__label" role="heading" aria-level={3}>
              {g.label}
            </span>
            <div className="tab-group__tabs">
              {g.sections.map((value) => {
                const s = SECTION_BY_VALUE.get(value);
                if (!s) return null;
                if (s.disabled) {
                  return (
                    <button
                      key={s.value}
                      className="tab tab--disabled"
                      disabled
                      aria-disabled="true"
                      title="This feature is planned — see roadmap"
                    >
                      {s.label}
                      <span className="tab-badge">Coming soon</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={s.value}
                    className={clsx('tab', section === s.value && 'active')}
                    onClick={() => setSection(s.value)}
                  >
                    {s.label}
                    {section === s.value && dirty && <span style={{ marginLeft: 4, color: 'var(--warning-primary)' }}>•</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {isUsersTab && <UsersPanel />}
      {isGroupsTab && <GroupsPanel />}
      {isOrgsTab && <OrganizationsPanel />}
      {isGlobalRolesTab && <GlobalRolesPanel />}
      {isToolsTab && <ToolsPanel onNavigate={onNavigate} nestSlug={nestSlug} />}
      {isToolPaletteTab && <ToolPalettePanel />}
      {isBundlesTab && <BundlesPanel />}
      {isPatsTab && <PatsPanel />}
      {isAuditTab && <AuditPanel />}
      {isRecycleBinTab && <RecycleBinPanel />}
      {isDataPipelineTab && <DataPipelinePlaceholder />}
      {!isPanelTab && q.isLoading && <SkeletonRows rows={6} />}
      {!isPanelTab && q.data && draft && (
        <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', maxWidth: 720 }}>
          <SectionFields section={section} values={draft} onChange={setField} canManageSettings={canManageSettings} />
        </section>
      )}
      {!isPanelTab && q.data?.updatedAt && (
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 12 }}>
          Last updated {new Date(q.data.updatedAt).toLocaleString()}{q.data.updatedBy && ` by ${q.data.updatedBy}`}
        </div>
      )}
    </>
  );
}

function SectionFields({
  section,
  values,
  onChange,
  canManageSettings = false,
}: {
  section: Section;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  /** CTSK-504 — settings:manage; gates the search tuning + health admin UI. */
  canManageSettings?: boolean;
}) {
  switch (section) {
    case 'general': return (
      <>
        <SelectField label="Display language" k="displayLanguage" values={values} onChange={onChange} options={[
          { value: 'system', label: 'System default' },
          { value: 'en', label: 'English' },
        ]} />
        <SelectField label="Date display" k="dateDisplay" values={values} onChange={onChange} options={[
          { value: 'system', label: 'System default' },
          { value: 'short', label: 'Short (5/12/26)' },
          { value: 'medium', label: 'Medium (May 12, 2026)' },
          { value: 'long', label: 'Long (May 12, 2026, 9:30 AM)' },
        ]} />
        <NumberField label="Items per page (lists)" k="itemsPerPage" values={values} onChange={onChange} />
        <ToggleField label="Slim sidebar" k="slimSidebar" values={values} onChange={onChange} />
        <SelectField label="Dark mode" k="darkMode" values={values} onChange={onChange} options={[
          { value: 'system', label: 'Match system' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]} />
        <ToggleField label="Invert thumbnails in dark mode" k="invertThumbnails" values={values} onChange={onChange} />
        <OrgThemeColorField />
        <ToggleField label="Show document counts in sidebar" k="showDocumentCountsInSidebar" values={values} onChange={onChange} />
        <ToggleField label="Warn before leaving with unsaved changes" k="warnOnUnsavedChanges" values={values} onChange={onChange} />
      </>
    );
    case 'documents': return (
      <>
        <CardEffectField />
        <ToggleField label="Use browser's PDF viewer (instead of embedded)" k="useBrowserPdfViewer" values={values} onChange={onChange} />
        <SelectField label="Default zoom" k="defaultZoom" values={values} onChange={onChange} options={[
          { value: 'fit_width', label: 'Fit width' },
          { value: 'fit_page', label: 'Fit page' },
          { value: '100', label: '100%' },
          { value: '150', label: '150%' },
          { value: '200', label: '200%' },
        ]} />
        <ToggleField label="Remove inbox tags on save" k="removeInboxTagsOnSave" values={values} onChange={onChange} />
        <ToggleField label="Show thumbnail while document content loads" k="showThumbnailDuringLoad" values={values} onChange={onChange} />
        <SelectField label="Global search scope" k="globalSearchScope" values={values} onChange={onChange} options={[
          { value: 'title', label: 'Title only' },
          { value: 'title_and_content', label: 'Title + content (default)' },
        ]} />
        <ToggleField label="Show confirmation for bulk edits" k="showBulkConfirmationDialogs" values={values} onChange={onChange} />
        <ToggleField label="Apply bulk edits on close" k="applyBulkOnClose" values={values} onChange={onChange} />
        <ToggleField label="Enable notes" k="enableNotes" values={values} onChange={onChange} />
        <ToggleField label="Enable document grouping (Group / Collection + Bulk Edit)" k="groupingEnabled" values={values} onChange={onChange} />
        {/* CTSK-501 — folder capture. Clamped server-side on read and write. */}
        <LinesField
          label="Folder drops: names to ignore"
          k="groupingIgnore"
          values={values}
          onChange={onChange}
          hint="One file or folder name per line, matched exactly (case-sensitive) at any depth. An ignored folder is skipped with everything in it."
        />
        <NumberField
          label="Folder drops: maximum files per drop"
          k="groupingMaxFilesPerDrop"
          values={values}
          onChange={onChange}
          hint="1–5000, default 1000. Files beyond the limit are not uploaded. The dialog warns above 500 files."
        />
        <SelectField
          label="Folder drops: name a group from the folder"
          k="groupingAutoNameFromFolder"
          values={values}
          onChange={onChange}
          hint="Uses the dropped folder's own name (not subfolder names) and applies it once the upload has finished."
          options={[
            { value: 'off', label: 'Off' },
            { value: 'prompt', label: 'Ask each time (default)' },
            { value: 'always', label: 'Always' },
          ]}
        />
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 16 }}>
          Search ranking, facets, telemetry and index health now live on the Search tab.
        </p>
      </>
    );
    case 'search': return (
      <>
        {/* CTSK-523 — grouping gate. Same `groupingEnabled` key as the Documents
            tab (stable key; two surfaces): label weight + facets only apply
            while grouping is on, so the gate is shown where those controls live. */}
        <ToggleField label="Enable document grouping (gates label weight and facet counts)" k="groupingEnabled" values={values} onChange={onChange} />
        {/* CTSK-504 — search tuning + health. Admin only; both render nothing otherwise. */}
        <SearchTuningSection values={values} onChange={onChange} canEdit={canManageSettings} defaultOpen />
        <SearchHealthCard canView={canManageSettings} />
        {!canManageSettings && (
          <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Search tuning and index health are available to administrators (<code>settings:manage</code>).
          </p>
        )}
      </>
    );
    case 'data-pipeline': return null;
    case 'notifications': return (
      <>
        <ToggleField label="Email notifications enabled" k="emailEnabled" values={values} onChange={onChange} />
        <ToggleField label="In-app notifications enabled" k="inAppEnabled" values={values} onChange={onChange} />
        <SelectField label="Digest schedule" k="digestSchedule" values={values} onChange={onChange} options={[
          { value: 'never', label: 'Never (only as events fire)' },
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
        ]} />
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 16 }}>
          Per-event toggles and email templates land in Phase 7.
        </p>
      </>
    );
    case 'permissions': return (
      <>
        <NumberField label="Session lifetime (seconds)" k="sessionLifetimeSeconds" values={values} onChange={onChange} />
        <NumberField label="Soft-delete retention (days)" k="softDeleteRetentionDays" values={values} onChange={onChange} />
        <ToggleField label="Require 2FA for admin actions" k="require2faForAdmin" values={values} onChange={onChange} />
        <ToggleField label="Personal Access Tokens (PAT) enabled" k="patEnabled" values={values} onChange={onChange} />
        <p style={{ color: 'var(--warning-primary)', fontSize: 12, marginTop: 16 }}>
          Saving this section requires the <code>settings:manage_security</code> capability.
        </p>
      </>
    );
  }
}

// CTSK-523 — Settings → Data Pipeline. Deliberately empty: reserves the tab
// and the /settings/data-pipeline route for a future real surface. Static copy
// only — it must not fetch or expose any pipeline internals.
function DataPipelinePlaceholder() {
  return (
    <section
      data-testid="data-pipeline-placeholder"
      style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', maxWidth: 720 }}
    >
      <p style={{ margin: 0, fontSize: 14 }}>
        Data Pipeline configuration — coming soon. Pipeline stage ordering, enrichment toggles, and job-queue tuning will land here.
      </p>
    </section>
  );
}

// ---------- field components ----------

function FieldShell({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function ToggleField({ label, k, values, onChange }: { label: string; k: string; values: Record<string, unknown>; onChange: (k: string, v: unknown) => void }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={!!values[k]} onChange={(e) => onChange(k, e.target.checked)} />
        {label}
      </label>
    </div>
  );
}

function TextField({ label, k, values, onChange, placeholder }: { label: string; k: string; values: Record<string, unknown>; onChange: (k: string, v: unknown) => void; placeholder?: string }) {
  return (
    <FieldShell label={label}>
      <input
        type="text"
        className="settings-control"
        value={(values[k] as string) ?? ''}
        onChange={(e) => onChange(k, e.target.value)}
        placeholder={placeholder}
      />
    </FieldShell>
  );
}

/**
 * CTSK-501 — a string-array setting edited as one name per line. Edits stay
 * local while typing (a trailing newline must survive) and are committed to
 * the form on blur.
 */
function LinesField({ label, k, values, onChange, hint }: { label: string; k: string; values: Record<string, unknown>; onChange: (k: string, v: unknown) => void; hint?: string }) {
  const arr = Array.isArray(values[k]) ? (values[k] as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  const joined = arr.join('\n');
  const [text, setText] = useState(joined);
  useEffect(() => { setText(joined); }, [joined]);
  return (
    <FieldShell label={label} hint={hint}>
      <textarea
        rows={6}
        className="settings-control"
        value={text}
        aria-label={label}
        spellCheck={false}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(k, text.split('\n').map((l) => l.trim()).filter((l) => l !== ''))}
        style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
      />
    </FieldShell>
  );
}

function NumberField({ label, k, values, onChange, step, hint }: { label: string; k: string; values: Record<string, unknown>; onChange: (k: string, v: unknown) => void; step?: number; hint?: string }) {
  return (
    <FieldShell label={label} hint={hint}>
      <input
        type="number"
        className="settings-control"
        step={step}
        value={(values[k] as number) ?? ''}
        onChange={(e) => onChange(k, e.target.value === '' ? null : Number(e.target.value))}
      />
    </FieldShell>
  );
}

function SelectField({ label, k, values, onChange, options, hint }: { label: string; k: string; values: Record<string, unknown>; onChange: (k: string, v: unknown) => void; options: { value: string; label: string }[]; hint?: string }) {
  return (
    <FieldShell label={label} hint={hint}>
      <select
        className="settings-control"
        value={(values[k] as string) ?? ''}
        onChange={(e) => onChange(k, e.target.value)}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldShell>
  );
}

/** Named theme presets the operator can pick instead of fishing for a hex
 *  value. Each just sets the underlying `themeColor` string — there's no
 *  separate "preset" setting persisted. The button highlight is purely
 *  visual: whichever color matches the current value gets a ring. */
const THEME_PRESETS: { name: string; color: string }[] = [
  { name: 'Emerald (default)', color: '#2F6B4E' },
  { name: 'Indigo',            color: '#3B4CCA' },
  { name: 'Slate',             color: '#475569' },
  { name: 'Amber',             color: '#B45309' },
  { name: 'Crimson',           color: '#B3261E' },
  { name: 'Teal',              color: '#0F766E' },
  { name: 'Violet',            color: '#7A4D8E' },
  { name: 'Ocean',             color: '#2A5C8C' },
];
const DEFAULT_THEME_COLOR = '#2F6B4E';

/** CTSK-283 — the theme picker saves PER ORG now (PATCH /orgs/:id
 *  { themeColor }), replacing the old app-wide General-envelope value
 *  whose last-saved color painted every org. Self-contained field:
 *  it owns its query + mutation, so it sits outside the envelope's
 *  draft/Save flow — picking a color saves immediately with a
 *  "saved to org" toast and repaints the accent on the spot.
 *  PATCH /orgs/:id is per-org-owner gated server-side; non-owners see
 *  the picker disabled with a hint instead of a 403 surprise. */
function OrgThemeColorField() {
  const qc = useQueryClient();
  const { session } = useSession();
  const activeOrgId = session?.activeOrg?.id ?? null;
  const orgName = session?.activeOrg?.name ?? session?.activeOrg?.slug ?? '';

  // The my-orgs list carries myMembership.isOwner + a fresh themeColor
  // (the session copy goes stale after a save — invalidating this
  // query keeps the field itself truthful).
  const orgsQ = useQuery({
    queryKey: ['my-orgs', { includeHidden: false }],
    queryFn: () => apiList<{ id: string; themeColor?: string | null; myMembership?: { isOwner: boolean } }>(
      '/orgs',
    ),
    enabled: !!activeOrgId,
  });
  const activeRow = orgsQ.data?.data.find((o) => o.id === activeOrgId);
  const isOwner = activeRow?.myMembership?.isOwner ?? false;
  const stored = activeRow !== undefined
    ? activeRow.themeColor ?? null
    : session?.activeOrg?.themeColor ?? null;

  const save = useMutation({
    mutationFn: (themeColor: string | null) =>
      apiSingle<{ themeColor: string | null }>(`/orgs/${activeOrgId}`, {
        method: 'PATCH',
        body: { themeColor },
      }),
    onSuccess: (org) => {
      // Repaint immediately — no reload needed for the saving org.
      applyAccentColor(org.themeColor);
      toast.success(
        org.themeColor
          ? `Theme color saved to org: ${orgName}`
          : `Theme color reset to default for org: ${orgName}`,
      );
      qc.invalidateQueries({ queryKey: ['my-orgs'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!activeOrgId) {
    return (
      <FieldShell label="Theme color">
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          No active org — the theme color is a per-org setting.
        </span>
      </FieldShell>
    );
  }

  const current = stored ?? DEFAULT_THEME_COLOR;
  const isDefault = stored == null || current.toLowerCase() === DEFAULT_THEME_COLOR.toLowerCase();
  const disabled = !isOwner || save.isPending;
  const pick = (color: string) => {
    if (disabled) return;
    if (color.toLowerCase() === current.toLowerCase() && stored != null) return;
    save.mutate(color);
  };

  return (
    <FieldShell
      label="Theme color"
      hint={`Saved per organization${orgName ? ` — currently ${orgName}` : ''}. Switching orgs applies that org's color; unset orgs use the default.`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: disabled && !save.isPending ? 0.6 : 1 }}>
        {/* Preset palette — most operators just want one of these. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {THEME_PRESETS.map((p) => {
            const active = p.color.toLowerCase() === current.toLowerCase();
            return (
              <button
                key={p.color}
                type="button"
                onClick={() => pick(p.color)}
                disabled={disabled}
                title={p.name}
                aria-label={`Preset: ${p.name}`}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: p.color,
                  border: `2px solid ${active ? 'var(--text-primary)' : 'transparent'}`,
                  boxShadow: active ? '0 0 0 2px var(--surface-raised)' : undefined,
                  cursor: disabled ? 'not-allowed' : 'pointer', padding: 0,
                }}
              />
            );
          })}
        </div>

        {/* Custom hex via the native picker, plus a text input so the user
            can paste a value AND a reset button (clears the org override
            back to NULL = app default). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="color"
            value={current}
            onChange={(e) => pick(e.target.value)}
            disabled={disabled}
            style={{ width: 44, height: 32, border: '1px solid var(--border-strong)', borderRadius: 4, padding: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
            aria-label="Custom theme color"
          />
          <input
            type="text"
            defaultValue={current}
            key={current}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9a-fA-F]{6}$/.test(v)) pick(v);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const v = (e.target as HTMLInputElement).value.trim();
              if (/^#[0-9a-fA-F]{6}$/.test(v)) pick(v);
            }}
            disabled={disabled}
            placeholder="#2F6B4E"
            style={{
              width: 100, padding: '4px 8px', fontFamily: 'var(--font-mono)', fontSize: 12,
              border: '1px solid var(--border-strong)', borderRadius: 4,
            }}
          />
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => save.mutate(null)}
            disabled={disabled || isDefault}
            style={{ fontSize: 12, padding: '4px 10px' }}
          >
            Reset
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {save.isPending
              ? 'Saving…'
              : !isOwner
                ? 'Only org owners can change the org theme.'
                : 'applies to buttons, links, tabs, and accents'}
          </span>
        </div>
      </div>
    </FieldShell>
  );
}
